import React, { useEffect, useState } from "react";
import {
    Box, Card, CardContent, Typography, CircularProgress,
    Chip, Skeleton, Tooltip as MuiTooltip,
} from "@mui/material";
import {
    ResponsiveContainer,
    BarChart, Bar,
    AreaChart, Area,
    XAxis, YAxis, CartesianGrid,
    Tooltip, Legend, defs, linearGradient,
} from "recharts";
import { API_ENDPOINTS, getData } from "../../services/apiService.js";
import { useNavigate } from "react-router-dom";
import {
    LocalShippingRounded as TruckIcon,
    DirectionsCarRounded as CarIcon,
    CurrencyRupeeRounded as RupeeIcon,
    PeopleRounded as PeopleIcon,
    FmdGoodRounded as PinIcon,
    Inventory2Rounded as BoxIcon,
    TrendingUpRounded as TrendUpIcon,
    TrendingDownRounded as TrendDownIcon,
    RefreshRounded as RefreshIcon,
    CalendarTodayRounded as CalIcon,
} from "@mui/icons-material";

// ── Brand palette ─────────────────────────────────────────────────────────
const T = {
    teal:    "#007C84",
    tealDk:  "#016268",
    tealLt:  "#E0F4F5",
    orange:  "#FF7900",
    orangeLt:"#FFF0E0",
    indigo:  "#4F46E5",
    indigoLt:"#EEF2FF",
    emerald: "#059669",
    emeraldLt:"#D1FAE5",
    rose:    "#E11D48",
    roseLt:  "#FFE4E6",
    amber:   "#D97706",
    amberLt: "#FEF3C7",
    bg:      "#F7FAFC",
    surface: "#FFFFFF",
    border:  "#E2E8F0",
    text:    "#0F172A",
    muted:   "#64748B",
};

// ── Range toggle ──────────────────────────────────────────────────────────
const RANGES = [
    { label: "Today",  value: "today" },
    { label: "Week",   value: "week"  },
    { label: "Month",  value: "month" },
    { label: "Year",   value: "year"  },
];

const RangeToggle = ({ value, onChange }) => (
    <Box sx={{ display: "flex", gap: 0.5, bgcolor: T.bg, borderRadius: "10px", p: 0.5 }}>
        {RANGES.map(r => (
            <Box
                key={r.value}
                onClick={() => onChange(r.value)}
                sx={{
                    px: 1.5, py: 0.5, borderRadius: "8px", fontSize: 12, fontWeight: 600,
                    cursor: "pointer", transition: "all 0.15s",
                    bgcolor: value === r.value ? T.teal : "transparent",
                    color:   value === r.value ? "#fff"  : T.muted,
                    "&:hover": { bgcolor: value === r.value ? T.teal : T.tealLt, color: value === r.value ? "#fff" : T.teal },
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                }}
            >
                {r.label}
            </Box>
        ))}
    </Box>
);

// ── KPI card ──────────────────────────────────────────────────────────────
const KpiCard = ({ title, value, icon, iconBg, iconColor, route, loading, accent }) => {
    const navigate = useNavigate();
    return (
        <Card
            onClick={() => route && navigate(route)}
            sx={{
                flex: "1 1 0", minWidth: 160,
                borderRadius: "16px", cursor: route ? "pointer" : "default",
                border: `1px solid ${T.border}`,
                boxShadow: "0 1px 4px rgba(15,23,42,0.06)",
                transition: "transform 0.18s, box-shadow 0.18s",
                "&:hover": route ? {
                    transform: "translateY(-3px)",
                    boxShadow: "0 8px 24px rgba(0,124,132,0.14)",
                } : {},
                overflow: "hidden",
                position: "relative",
            }}
        >
            {/* Accent stripe */}
            <Box sx={{ height: 3, background: `linear-gradient(90deg, ${accent || T.teal}, ${accent ? accent + "99" : T.tealLt})` }} />
            <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <Box>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.muted, letterSpacing: "0.3px", textTransform: "uppercase", mb: 1, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                            {title}
                        </Typography>
                        {loading
                            ? <Skeleton width={80} height={36} />
                            : <Typography sx={{ fontSize: 26, fontWeight: 800, color: T.text, lineHeight: 1.1, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                                {value}
                            </Typography>
                        }
                    </Box>
                    <Box sx={{
                        width: 44, height: 44, borderRadius: "12px",
                        bgcolor: iconBg, display: "flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0,
                    }}>
                        {React.cloneElement(icon, { sx: { fontSize: 22, color: iconColor } })}
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

// ── Custom chart tooltip ──────────────────────────────────────────────────
const ChartTooltip = ({ active, payload, label, rupee }) => {
    if (!active || !payload?.length) return null;
    return (
        <Box sx={{
            bgcolor: "#fff", border: `1px solid ${T.border}`, borderRadius: "10px",
            p: 1.5, boxShadow: "0 4px 16px rgba(0,0,0,0.10)", minWidth: 140,
        }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: T.muted, mb: 0.5 }}>{label}</Typography>
            {payload.map(p => (
                <Box key={p.dataKey} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.3 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: p.color }} />
                    <Typography sx={{ fontSize: 12, color: T.text, fontWeight: 600 }}>
                        {p.name}: {rupee ? `₹${Number(p.value).toLocaleString("en-IN")}` : p.value}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
};

// ── Chart card wrapper ────────────────────────────────────────────────────
const ChartCard = ({ title, subtitle, range, onRangeChange, loading, children }) => (
    <Card sx={{
        flex: "1 1 0", borderRadius: "16px",
        border: `1px solid ${T.border}`,
        boxShadow: "0 1px 4px rgba(15,23,42,0.06)",
    }}>
        <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2.5 }}>
                <Box>
                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: T.text, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                        {title}
                    </Typography>
                    {subtitle && (
                        <Typography sx={{ fontSize: 12, color: T.muted, mt: 0.3 }}>{subtitle}</Typography>
                    )}
                </Box>
                <RangeToggle value={range} onChange={onRangeChange} />
            </Box>
            <Box sx={{ height: 280, display: "flex", alignItems: "center", justifyContent: "center" }}>
                {loading
                    ? <CircularProgress size={32} sx={{ color: T.teal }} />
                    : children
                }
            </Box>
        </CardContent>
    </Card>
);

// ── Main Dashboard ────────────────────────────────────────────────────────
export default function Dashboard() {
    const [summary, setSummary]                   = useState(null);
    const [summaryLoading, setSummaryLoading]     = useState(true);
    const [barRange, setBarRange]                 = useState("month");
    const [barData, setBarData]                   = useState([]);
    const [barLoading, setBarLoading]             = useState(false);
    const [lineRange, setLineRange]               = useState("month");
    const [lineData, setLineData]                 = useState([]);
    const [lineLoading, setLineLoading]           = useState(false);
    const navigate = useNavigate();

    const rangeParam = r =>
        r === "today" ? "today" : r === "week" ? "this_week" : r === "month" ? "this_month" : "this_year";

    useEffect(() => {
        setSummaryLoading(true);
        getData(API_ENDPOINTS.dashboardSummary)
            .then(r => setSummary(r.result))
            .catch(() => {})
            .finally(() => setSummaryLoading(false));
    }, []);

    useEffect(() => {
        setBarLoading(true);
        getData(API_ENDPOINTS.tripAnalytics, { filter: rangeParam(barRange) })
            .then(r => setBarData(r.result?.chartData || []))
            .catch(() => setBarData([]))
            .finally(() => setBarLoading(false));
    }, [barRange]);

    useEffect(() => {
        setLineLoading(true);
        getData(API_ENDPOINTS.getRevenueChart, { filter: rangeParam(lineRange) })
            .then(r => setLineData(r.result?.chartData || []))
            .catch(() => setLineData([]))
            .finally(() => setLineLoading(false));
    }, [lineRange]);

    const fmtDate = d => new Date(d).toLocaleDateString("en-GB", { month: "short", day: "numeric" });
    const fmtNum  = n => n != null ? Number(n).toLocaleString("en-IN") : "--";
    const fmtRs   = n => n != null ? `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}` : "--";

    const tripData    = barData.map(d => ({ date: fmtDate(d.date), Trips: d.tripCount }));
    const revenueData = lineData.map(d => ({
        date: fmtDate(d.date),
        Total: d.totalRevenue, COD: d.cod, Wallet: d.wallet, Online: d.razorpay,
    }));

    const kpis = [
        { title: "Total Trips",      value: fmtNum(summary?.totalTrips),      icon: <TruckIcon />, iconBg: T.tealLt,    iconColor: T.teal,   accent: T.teal,   route: "/app/trips" },
        { title: "Active Trucks",    value: fmtNum(summary?.totalTrucks),     icon: <CarIcon  />, iconBg: T.emeraldLt,  iconColor: T.emerald,accent: T.emerald,route: "/app/trucks" },
        { title: "Total Revenue",    value: fmtRs(summary?.totalRevenue),     icon: <RupeeIcon/>, iconBg: T.indigoLt,  iconColor: T.indigo, accent: T.indigo, route: "/app/payments" },
        { title: "Platform Revenue", value: fmtRs(summary?.platformRevenue),  icon: <RupeeIcon/>, iconBg: T.orangeLt,  iconColor: T.orange, accent: T.orange, route: "/app/payments" },
        { title: "Active Drivers",   value: fmtNum(summary?.totalDrivers),    icon: <PeopleIcon/>,iconBg: T.amberLt,   iconColor: T.amber,  accent: T.amber,  route: "/app/drivers" },
        { title: "Cities Covered",   value: fmtNum(summary?.totalCities),     icon: <PinIcon  />, iconBg: T.roseLt,    iconColor: T.rose,   accent: T.rose,   route: "/app/delivery-zones" },
    ];

    const now = new Date();
    const greeting = now.getHours() < 12 ? "Good morning" : now.getHours() < 17 ? "Good afternoon" : "Good evening";
    const adminName = (() => { try { return JSON.parse(localStorage.getItem("userData") || "{}")?.first_name || "Admin"; } catch { return "Admin"; } })();

    return (
        <Box sx={{ bgcolor: T.bg, minHeight: "100vh", pb: 4 }}>

            {/* ── Hero header ── */}
            <Box sx={{
                background: `linear-gradient(135deg, ${T.teal} 0%, ${T.tealDk} 100%)`,
                borderRadius: "20px", p: 3, mb: 3,
                display: "flex", justifyContent: "space-between", alignItems: "center",
                boxShadow: "0 4px 20px rgba(0,124,132,0.2)",
            }}>
                <Box>
                    <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#fff", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                        {greeting}, {adminName} 👋
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.75)", mt: 0.5 }}>
                        Here's what's happening with your fleet today.
                    </Typography>
                </Box>
                <Box sx={{ textAlign: "right" }}>
                    <Chip
                        icon={<CalIcon sx={{ fontSize: "14px !important", color: "rgba(255,255,255,0.8) !important" }} />}
                        label={now.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                        sx={{
                            bgcolor: "rgba(255,255,255,0.15)", color: "#fff",
                            border: "1px solid rgba(255,255,255,0.25)",
                            fontWeight: 600, fontSize: 12,
                            fontFamily: "'Plus Jakarta Sans', sans-serif",
                        }}
                    />
                    {summary?.lastUpdated && (
                        <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.55)", mt: 0.8 }}>
                            Updated {new Date(summary.lastUpdated).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                        </Typography>
                    )}
                </Box>
            </Box>

            {/* ── KPI row ── */}
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
                {kpis.map(k => (
                    <KpiCard key={k.title} {...k} loading={summaryLoading} />
                ))}
            </Box>

            {/* ── Charts row ── */}
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>

                {/* ── Trip bar chart ── */}
                <ChartCard
                    title="Trip Activity"
                    subtitle="Number of trips completed per period"
                    range={barRange}
                    onRangeChange={setBarRange}
                    loading={barLoading}
                >
                    {tripData.length === 0
                        ? <Typography color="text.secondary" fontSize={13}>No data for this period</Typography>
                        : (
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={tripData} barSize={28} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                                    <defs>
                                        <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%"   stopColor={T.teal}   stopOpacity={1} />
                                            <stop offset="100%" stopColor={T.tealDk} stopOpacity={0.85} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="4 4" stroke="#E2E8F0" vertical={false} />
                                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: T.muted }} axisLine={false} tickLine={false} />
                                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: T.muted }} axisLine={false} tickLine={false} width={28} />
                                    <Tooltip content={<ChartTooltip />} cursor={{ fill: T.tealLt }} />
                                    <Bar dataKey="Trips" fill="url(#barGrad)" radius={[6, 6, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        )
                    }
                </ChartCard>

                {/* ── Revenue area chart ── */}
                <ChartCard
                    title="Revenue Breakdown"
                    subtitle="Total and per-method revenue over time"
                    range={lineRange}
                    onRangeChange={setLineRange}
                    loading={lineLoading}
                >
                    {revenueData.length === 0
                        ? <Typography color="text.secondary" fontSize={13}>No data for this period</Typography>
                        : (
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={revenueData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                                    <defs>
                                        <linearGradient id="areaTotal" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%"   stopColor={T.teal}   stopOpacity={0.25} />
                                            <stop offset="95%"  stopColor={T.teal}   stopOpacity={0} />
                                        </linearGradient>
                                        <linearGradient id="areaOnline" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%"   stopColor={T.orange} stopOpacity={0.2} />
                                            <stop offset="95%"  stopColor={T.orange} stopOpacity={0} />
                                        </linearGradient>
                                        <linearGradient id="areaCOD" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%"   stopColor={T.indigo} stopOpacity={0.2} />
                                            <stop offset="95%"  stopColor={T.indigo} stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="4 4" stroke="#E2E8F0" vertical={false} />
                                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: T.muted }} axisLine={false} tickLine={false} />
                                    <YAxis tick={{ fontSize: 11, fill: T.muted }} axisLine={false} tickLine={false}
                                        tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} width={36} />
                                    <Tooltip content={<ChartTooltip rupee />} />
                                    <Legend wrapperStyle={{ fontSize: 12, fontFamily: "'Plus Jakarta Sans', sans-serif", paddingTop: 8 }} />
                                    <Area type="monotone" dataKey="Total"  stroke={T.teal}   strokeWidth={2.5} fill="url(#areaTotal)"  name="Total"  dot={false} activeDot={{ r: 4, strokeWidth: 0 }} />
                                    <Area type="monotone" dataKey="Online" stroke={T.orange} strokeWidth={2}   fill="url(#areaOnline)" name="Online" dot={false} activeDot={{ r: 4, strokeWidth: 0 }} />
                                    <Area type="monotone" dataKey="COD"    stroke={T.indigo} strokeWidth={2}   fill="url(#areaCOD)"    name="COD"    dot={false} activeDot={{ r: 4, strokeWidth: 0 }} />
                                    <Area type="monotone" dataKey="Wallet" stroke={T.emerald}strokeWidth={2}   fill="none"             name="Wallet" dot={false} activeDot={{ r: 4, strokeWidth: 0 }} />
                                </AreaChart>
                            </ResponsiveContainer>
                        )
                    }
                </ChartCard>
            </Box>
        </Box>
    );
}
